import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Vehicle } from '../../../models/VehicleModel';
import showToast from '../../../utils/toast';
import VehiclesTable from './VehiclesTable';
import ButtonCancelForm from '../../../components/forms/ButtonCancelForm';
import ButtonSaveForm from '../../../components/forms/ButtonSaveForm';
import ConfirmAlert from '../../../components/Base/ConfirmAlert';
import useVehiclesService from '../../../services/useVehiclesService';
import VehicleForm from '../../../components/Drivers/Vehicle/VehicleForm';
import { useFormErrors } from '../../../hooks/useFormErrors';
import Pagination from '../../../components/Base/Pagination';
import TableTextSearch from '../../../components/Base/TableTextSearch';
import MaintainerPageShell from '../../../components/Base/MaintainerPageShell';
import { Car, Plus } from 'lucide-react';

const VehiclesPage: React.FC = () => {
  const svc = useVehiclesService();
  const { list, store, update, remove, fetching } = svc;

  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 10,
    hasNext: false,
    hasPrevious: false,
  });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Vehicle | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [savingLocked, setSavingLocked] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [vehicleIdToDelete, setVehicleIdToDelete] = useState<number | null>(null);
  const activeRequest = useRef<AbortController | null>(null);
  const { fieldErrors, clearErrors, setErrorsFromResponse } = useFormErrors();
  const [searchText, setSearchText] = useState('');
  const searchTextRef = useRef(searchText);
  searchTextRef.current = searchText;
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback((page: number = 1, search?: string) => {
    activeRequest.current?.abort();
    const ctrl = new AbortController();
    activeRequest.current = ctrl;

    const filters: Record<string, unknown> = { page };
    const term = (search !== undefined ? search : searchTextRef.current).trim();
    if (term) filters.search = term;

    list(
      filters as Record<string, string | number>,
      {
        onSuccess: (resp: any) => {
          const data = resp?.data ?? {};
          const items = Array.isArray(data.vehicles) ? data.vehicles : data.items ?? [];
          setVehicles(items);

          if (data.pagination) {
            setPagination({
              page: data.pagination.page || page,
              totalPages: data.pagination.total_pages || 1,
              totalCount: data.pagination.total_count || 0,
              pageSize: data.pagination.page_size || 10,
              hasNext: data.pagination.has_next || false,
              hasPrevious: data.pagination.has_previous || false,
            });
          }
        },
        onError: (resp: any) => {
          if (resp?.http_status === 403) {
            showToast(
              resp?.message || 'No tienes permisos para acceder a esta sección.',
              'error'
            );
          } else {
            showToast(resp?.message ?? 'Error cargando vehículos', 'error');
          }
        },
      },
      ctrl.signal
    );
  }, [list]);

  useEffect(() => {
    load();
    return () => {
      activeRequest.current?.abort();
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [load]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchText(value);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => load(1, value), 400);
    },
    [load]
  );

  const openCreate = () => {
    setEditing({
      brand: '',
      license_plate: '',
      status: 'Operativo',
    });
    setFieldError(null);
    setShowForm(true);
  };

  const openEdit = (v: Vehicle) => {
    setEditing(v);
    setFieldError(null);
    setShowForm(true);
  };

  const callbackSave = async (): Promise<any> => {
    if (!editing) return;

    setFieldError(null);
    setSavingLocked(true);

    try {
      if (editing.id) {
        const r = await update(editing.id, {
          brand: editing.brand,
          license_plate: editing.license_plate,
          status: editing.status,
        });
        return r;
      } else {
        const r = await store({
          brand: editing.brand,
          license_plate: editing.license_plate,
          status: editing.status,
        });
        return r;
      }
    } catch (error: any) {
      const fields = error?.response?.data;

      if (fields?.errors) {
        setErrorsFromResponse(fields.errors);
        return;
      }

      setErrorsFromResponse(fields);
    } finally {
      setSavingLocked(false);
    }
  };

  const handleSaveComplete = (ok: boolean, resp?: any) => {
    if (ok) {
      showToast(resp?.message ?? 'Guardado correctamente', 'success');
      setShowForm(false);
      setEditing(null);
      clearErrors();
      load(pagination.page, searchTextRef.current.trim() || undefined);
      return;
    }
    setErrorsFromResponse(resp);
    showToast(resp?.message ?? 'Error guardando', 'error');
  };

  const openDeleteConfirmation = (id: number) => {
    setVehicleIdToDelete(id);
    setIsAlertOpen(true);
  };

  const handleConfirmDelete = () => {
    if (vehicleIdToDelete === null) return;

    remove(vehicleIdToDelete, {
      onSuccess: (r: any) => {
        showToast(r?.message ?? 'Eliminado', 'success');
        load(pagination.page, searchTextRef.current.trim() || undefined);
      },
      onError: (r: any) => showToast(r?.message ?? 'Error eliminando', 'error'),
    });

    setIsAlertOpen(false);
    setVehicleIdToDelete(null);
  };

  const handleClose = () => {
    clearErrors();
    setShowForm(false);
    setFieldError(null);
  };

  const handleCloseAlert = () => {
    setIsAlertOpen(false);
    setVehicleIdToDelete(null);
  };

  const totalLabel = fetching.list
    ? 'Cargando...'
    : (
      <span className="maintainer-page__meta-count">
        <span className="maintainer-page__meta-pill">
          {pagination.totalCount || vehicles.length}
        </span>
        vehículos
      </span>
    );

  return (
    <MaintainerPageShell
      title="Vehículos"
      icon={<Car size={22} strokeWidth={2.25} />}
      meta={totalLabel}
      actions={
        <button type="button" className="btn-create" onClick={openCreate}>
          <Plus size={16} strokeWidth={2.25} aria-hidden />
          Nuevo vehículo
        </button>
      }
    >
      <VehiclesTable
        data={vehicles}
        loading={fetching.list}
        onEdit={openEdit}
        onDelete={openDeleteConfirmation}
        totalCount={pagination.totalCount}
        searchTerm={searchText}
        onClearSearch={() => handleSearchChange('')}
        toolbar={
          <TableTextSearch
            value={searchText}
            onChange={handleSearchChange}
            placeholder="Buscar por patente o marca..."
            aria-label="Buscar vehículos por patente o marca"
          />
        }
        footer={
          pagination.totalPages > 1 ? (
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              totalCount={pagination.totalCount}
              pageSize={pagination.pageSize}
              onPageChange={(page) => {
                load(page, searchTextRef.current.trim() || undefined);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          ) : null
        }
      />

      {showForm && editing && (
        <div className="companies-modal">
          <div className="companies-modal-inner">
            <h3>{editing.id ? 'Editar vehículo' : 'Crear vehículo'}</h3>
            <VehicleForm
              value={editing}
              onChange={setEditing}
              error={fieldError || undefined}
              fieldErrors={fieldErrors}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <ButtonCancelForm callbackCancel={handleClose} locked={savingLocked} confirmCancel={false} />
              <ButtonSaveForm callbackSave={callbackSave} locked={savingLocked} onComplete={handleSaveComplete} />
            </div>
          </div>
        </div>
      )}

      <ConfirmAlert
        isOpen={isAlertOpen}
        title="¿Eliminar vehículo?"
        message="Esta acción es permanente y no se puede deshacer."
        onConfirm={handleConfirmDelete}
        onClose={handleCloseAlert}
      />
    </MaintainerPageShell>
  );
};

export default VehiclesPage;
